import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RatioWireIcon } from './WorkflowAspectRatioField';
import {
  WORKFLOW_PRESET_RATIOS,
  getPresetDimensions,
  getPresetRatioMeta,
  resolveRatioFraction,
} from '@/pages/ipe/workbench/constants/imageFissionAspectSize';

export default function ImageFissionAspectSizeField({ value, onChange, readOnly = false }) {
  const { t } = useTranslation('workbench');
  const aspectRatio = String(value?.aspectRatio || '2:3');
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);

  const isOriginal = aspectRatio === 'original';
  const presetMeta = useMemo(() => getPresetRatioMeta(aspectRatio), [aspectRatio]);
  const iconRatio = useMemo(() => {
    if (presetMeta) return presetMeta;
    const fraction = resolveRatioFraction(value);
    if (fraction) return fraction;
    return { w: 2, h: 3 };
  }, [presetMeta, value]);

  const displayRatioLabel = useMemo(() => {
    if (aspectRatio === 'original') return t('studioChrome.originalRatio');
    const preset = getPresetDimensions(aspectRatio);
    if (
      preset
      && preset.width === value?.outputWidth
      && preset.height === value?.outputHeight
    ) {
      return aspectRatio;
    }
    if (aspectRatio !== 'custom' && presetMeta) return aspectRatio;
    return t('studioChrome.customRatio');
  }, [aspectRatio, presetMeta, t, value?.outputHeight, value?.outputWidth]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const emitChange = useCallback((patch) => {
    onChange?.(patch);
  }, [onChange]);

  const selectRatio = useCallback((next) => {
    setMenuOpen(false);
    if (next === 'original') {
      emitChange({
        aspectRatio: 'original',
        customAspectWidth: undefined,
        customAspectHeight: undefined,
      });
      return;
    }
    const dims = getPresetDimensions(next);
    emitChange({
      aspectRatio: next,
      outputWidth: dims?.width ?? value?.outputWidth ?? 800,
      outputHeight: dims?.height ?? value?.outputHeight ?? 1200,
      customAspectWidth: undefined,
      customAspectHeight: undefined,
    });
  }, [emitChange, value?.outputHeight, value?.outputWidth]);

  const handleWidthChange = (event) => {
    const nextWidth = Math.max(1, Math.floor(Number(event.target.value) || 0));
    if (!nextWidth) return;
    const nextHeight = Math.max(1, Math.floor(Number(value?.outputHeight) || 0)) || nextWidth;
    const preset = getPresetDimensions(aspectRatio);
    const matchesPreset = preset
      && preset.width === nextWidth
      && preset.height === nextHeight;
    emitChange({
      outputWidth: nextWidth,
      outputHeight: nextHeight,
      aspectRatio: matchesPreset ? aspectRatio : 'custom',
      customAspectWidth: matchesPreset ? undefined : nextWidth,
      customAspectHeight: matchesPreset ? undefined : nextHeight,
    });
  };

  const handleHeightChange = (event) => {
    const nextHeight = Math.max(1, Math.floor(Number(event.target.value) || 0));
    if (!nextHeight) return;
    const nextWidth = Math.max(1, Math.floor(Number(value?.outputWidth) || 0)) || nextHeight;
    const preset = getPresetDimensions(aspectRatio);
    const matchesPreset = preset
      && preset.width === nextWidth
      && preset.height === nextHeight;
    emitChange({
      outputWidth: nextWidth,
      outputHeight: nextHeight,
      aspectRatio: matchesPreset ? aspectRatio : 'custom',
      customAspectWidth: matchesPreset ? undefined : nextWidth,
      customAspectHeight: matchesPreset ? undefined : nextHeight,
    });
  };

  const widthValue = isOriginal ? '' : (value?.outputWidth ?? '');
  const heightValue = isOriginal ? '' : (value?.outputHeight ?? '');

  return (
    <div className="img-fission-aspect-size" ref={rootRef}>
      <div className="img-fission-aspect-size__row">
        <div className="img-fission-aspect-size__ratio-wrap">
          <button
            type="button"
            className="img-fission-aspect-size__ratio-btn"
            disabled={readOnly}
            aria-expanded={menuOpen}
            aria-haspopup="listbox"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <RatioWireIcon w={iconRatio.w} h={iconRatio.h} />
            <span className="img-fission-aspect-size__ratio-label">
              {displayRatioLabel}
            </span>
            <span className="img-fission-aspect-size__ratio-chevron" aria-hidden="true">▾</span>
          </button>
          {menuOpen ? (
            <div className="img-fission-aspect-size__menu" role="listbox" aria-label={t('studioChrome.aspectSize')}>
              <button
                type="button"
                className={`img-fission-aspect-size__menu-item${aspectRatio === 'original' ? ' is-active' : ''}`}
                onClick={() => selectRatio('original')}
              >
                <RatioWireIcon w={4} h={3} />
                <span>{t('studioChrome.originalRatio')}</span>
              </button>
              <div className="img-fission-aspect-size__menu-grid">
                {WORKFLOW_PRESET_RATIOS.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    className={`img-fission-aspect-size__menu-grid-item${aspectRatio === item.value ? ' is-active' : ''}`}
                    onClick={() => selectRatio(item.value)}
                  >
                    <RatioWireIcon w={item.w} h={item.h} />
                    <span>{item.value}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <label className="img-fission-aspect-size__dim">
          <input
            type="number"
            min="1"
            step="1"
            className="img-fission-aspect-size__dim-input"
            value={widthValue}
            disabled={readOnly || isOriginal}
            onChange={handleWidthChange}
            aria-label={t('studioChrome.outputWidth')}
            placeholder={t('studioChrome.width')}
          />
          <span className="img-fission-aspect-size__dim-suffix">{t('studioChrome.width')}</span>
        </label>

        <label className="img-fission-aspect-size__dim">
          <input
            type="number"
            min="1"
            step="1"
            className="img-fission-aspect-size__dim-input"
            value={heightValue}
            disabled={readOnly || isOriginal}
            onChange={handleHeightChange}
            aria-label={t('studioChrome.outputHeight')}
            placeholder={t('studioChrome.height')}
          />
          <span className="img-fission-aspect-size__dim-suffix">{t('studioChrome.height')}</span>
        </label>
      </div>
    </div>
  );
}
